import { Button, DatePicker, Drawer, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useState } from "react";
import { Link } from "react-router-dom";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import FilterIconThree from "../../../assets/icons/FilterIconThree";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import { BASE_API_CASH_COLLECTION } from "../../../constants/apiUrl";
import { APP_API } from "../../../constants/constants";
import { useGetTransactionsSummaryQuery } from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../features/cashCollection/geoSetup/geoSetupApi";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  GetTransactionsSummaryDataParamsType,
  GetTransactionsSummaryDataType,
} from "../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";

export default function TransactionList() {
  const [open, setOpen] = useState(false);
  const { RangePicker } = DatePicker;
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetTransactionsSummaryDataParamsType>();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(
    filter?.zoneId ?? null
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(
    filter?.areaId ?? null
  );
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    filter?.territoryId ?? null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(
    filter?.routeId ?? null
  );

  const { isError, error, isLoading, data } = useGetTransactionsSummaryQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      limit,
    },
  });

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
    isLoading: getAllRouteIsLoading,
  } = useGetAllRouteQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        territoryId: selectedTerritoryId ? selectedTerritoryId : undefined,
      },
    },
    {
      skip: !selectedTerritoryId,
    }
  );

  useError(isError, error, "Get transaction list fail");
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleDownload = () => {
    downloadXlsx(
      APP_API + BASE_API_CASH_COLLECTION.getTransactionsSummary,
      {
        ...filter,
        skip: 0,
        page: page,
        limit: 10000000,
      },
      `Transactions List_${moment().format("DD MMMM YYYY")}_${moment().format(
        "h.mm.ss A"
      )}`,
      "rows",
      (item: GetTransactionsSummaryDataType["data"]["rows"][0]) => {
        return {
          "Field Officer": capitalize(item?.createdByUser?.username) || "-",
          Zone: capitalize(item?.zone?.zoneName) || "-",
          Area: capitalize(item?.area?.areaName) || "-",
          Territory: capitalize(item?.territory?.territoryName) || "-",
          Route: capitalize(item?.route?.routeName) || "-",
          "Collection (BDT)":
            item?.totalAmount !== undefined && item?.totalAmount !== null
              ? formatNumber(item?.totalAmount)
              : "0",
          "Deposit (BDT)":
            item?.totalDeposit !== undefined && item?.totalDeposit !== null
              ? formatNumber(item?.totalDeposit)
              : "0",
          "Remaining (BDT)":
            item?.totalRemaining !== undefined && item?.totalRemaining !== null
              ? formatNumber(item?.totalRemaining)
              : "0",
        };
      }
    );
  };

  const columns: ColumnsType<GetTransactionsSummaryDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap">Field Officer</div>,
      dataIndex: "createdByUser",
      align: "center",
      render: (val) => {
        return val?.username ? capitalize(val?.username) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zone",
      align: "center",
      render: (val) => {
        return val?.zoneName ? capitalize(val?.zoneName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "area",
      align: "center",
      render: (val) => {
        return val?.areaName ? capitalize(val?.areaName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Territory</div>,
      dataIndex: "territory",
      align: "center",
      render: (val) => {
        return val?.territoryName ? capitalize(val?.territoryName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Route</div>,
      dataIndex: "route",
      align: "center",
      render: (val) => {
        return val?.routeName ? capitalize(val?.routeName) : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Collection (BDT)</div>,
      dataIndex: "totalAmount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Deposit (BDT)</div>,
      dataIndex: "totalDeposit",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Remaining (BDT)</div>,
      align: "center",
      render: (record) => {
        return record
          ? formatNumber((record?.totalAmount || 0) - record?.totalDeposit)
          : "0";
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val, record: any) => (
        <div className="flex justify-center items-center gap-2">
          <Link
            to={`/admin/cash-transactions/details/${record?.createdByUser?.id}/${record?.route?.id}`}
          >
            <Button
              className="action-button disabled:!bg-[#ddd]"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="FO Transaction">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              {/* <Input
                suffix={<SearchIcon />}
                placeholder="Search"
                style={{ width: 205 }}
                allowClear
                onChange={handleSearch}
              /> */}
              <RangePicker
                allowClear
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    handleFilter("dateFrom", dates[0].format("YYYY-MM-DD"));
                    handleFilter(
                      "dateTo",
                      dates[1].add(1, "day").format("YYYY-MM-DD")
                    );
                  } else {
                    // reset if cleared
                    handleFilter("dateFrom", undefined);
                    handleFilter("dateTo", undefined);
                  }
                }}
              />

              <IconButton
                className="flex items-center text-[#7a8799]"
                icon={<FilterIconThree />}
                onClick={() => showDrawer()}
              >
                Filter
              </IconButton>

              <Button
                type="primary"
                className="middle-button"
                icon={<DownloadIcon />}
                onClick={handleDownload}
                loading={excelLoading}
              >
                Export
              </Button>
            </>
          }
        />
      </SidebarLayout>

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          Filter
        </div>

        <div>
          {/* Zone */}
          <Select
            placeholder="Select Zone"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-8"
            options={
              getAllZoneData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.zoneName),
                value: val?.id,
              })) || []
            }
            value={selectedZoneId}
            onChange={(value) => {
              setSelectedZoneId(value ?? null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Area */}
          <Select
            placeholder="Select Area"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedZoneId}
            options={
              getAllAreaData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.areaName),
                value: val?.id,
              })) || []
            }
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value ?? null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Territory */}
          <Select
            placeholder="Select Territory"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedAreaId}
            options={
              getAllTerritoryData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.territoryName),
                value: val?.id,
              })) || []
            }
            value={selectedTerritoryId}
            onChange={(value) => {
              setSelectedTerritoryId(value ?? null);
              setSelectedRouteId(null);
            }}
          />

          {/* Route */}
          <Select
            placeholder="Route"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedTerritoryId}
            options={
              getAllRouteData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.routeName),
                value: val?.id,
              })) || []
            }
            value={selectedRouteId}
            onChange={(value) => setSelectedRouteId(value ?? null)}
          />

          {/* Field Officer */}
          {/* <Select
              placeholder="Field Officer"
              suffixIcon={<SelectDownIcon />}
              showSearch
              allowClear
              className="w-full mt-5"
              options={
                getAllUserData?.data?.users?.map((user: any) => ({
                  label: capitalize(user.username),
                  value: user.id,
                })) || []
              }
              value={selectedUserId}
              onChange={(value) => setSelectedUserId(value ?? null)}
            /> */}
        </div>

        <Space className="w-full mt-8" size={12}>
          {/* Cancel → clear everything */}
          <Button
            className="normal-dark-button modal-button !h-[40px]"
            onClick={() => {
              setSelectedZoneId(null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
              handleFilter("zoneId", undefined);
              handleFilter("areaId", undefined);
              handleFilter("territoryId", undefined);
              handleFilter("routeId", undefined);
              // handleFilter("userId", undefined);
              onClose();
            }}
            size="middle"
          >
            Cancel
          </Button>

          {/* Apply Filter */}
          <Button
            className="modal-button !h-[40px]"
            type="primary"
            size="middle"
            onClick={() => {
              handleFilter("zoneId", selectedZoneId ?? undefined);
              handleFilter("areaId", selectedAreaId ?? undefined);
              handleFilter("territoryId", selectedTerritoryId ?? undefined);
              handleFilter("routeId", selectedRouteId ?? undefined);

              onClose();
            }}
          >
            Filter
          </Button>
        </Space>
      </Drawer>
    </>
  );
}
